
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Industries', href: '/industries' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={handleLinkClick} className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/fa77cf8cc3bbd77c4d454e1d51d6ad11/34f7ef3fd990c9497763245b06aa73c4.png" 
                alt="TechCozo Logo" 
                className="h-12 w-12 object-contain transition-all duration-300 group-hover:scale-110 bg-transparent rounded-xl"
                style={{ backgroundColor: 'transparent' }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
            </div>
            <span className={`text-2xl font-bold transition-all duration-300 ${
              isScrolled 
                ? 'bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent' 
                : 'text-white'
            }`}>
              TechCozo
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={handleLinkClick}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap relative overflow-hidden group ${
                  isActive(item.href)
                    ? isScrolled
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 text-white shadow-lg'
                      : 'bg-white/20 text-white backdrop-blur-sm'
                    : isScrolled
                      ? 'text-gray-700 hover:text-teal-600 hover:bg-teal-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                <span className="relative z-10">{item.name}</span>
                {!isActive(item.href) && (
                  <div className={`absolute inset-0 ${
                    isScrolled 
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-green-500' 
                      : 'bg-white/20'
                  } opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                )}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:01618837750"
              className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
                isScrolled 
                  ? 'text-gray-600 hover:text-teal-600 hover:bg-teal-50' 
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-phone-line text-lg"></i>
            </a>
            <Link
              to="/contact"
              onClick={handleLinkClick}
              className="group relative px-6 py-3 bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 text-white rounded-full font-semibold overflow-hidden transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <span className="relative z-10">Get Started</span>
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 via-teal-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-all duration-300 ${
              isScrolled 
                ? 'text-gray-600 hover:text-teal-600 hover:bg-teal-50' 
                : 'text-white/90 hover:text-white hover:bg-white/10'
            }`}
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100/20 bg-white/95 backdrop-blur-md rounded-b-2xl">
            <nav className="flex flex-col space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleLinkClick}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-600 ${
                    isActive(item.href)
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 text-white'
                      : 'text-gray-700 hover:text-teal-600 hover:bg-teal-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-100 mt-4">
                <Link
                  to="/contact"
                  onClick={handleLinkClick}
                  className="block w-full bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 text-white px-4 py-3 rounded-lg font-semibold text-center"
                >
                  Get Started
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
